import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Bell, Loader2 } from 'lucide-react';
import { useNotificationPreferences, NotificationType } from '@/hooks/useNotificationPreferences';
import { Separator } from '@/components/ui/separator';

interface NotificationRow {
  type: NotificationType;
  label: string;
  icon: string;
  trigger: string;
}

const NOTIFICATION_TYPES: NotificationRow[] = [
  { type: 'lead', label: 'Nieuwe leads', icon: '📥', trigger: 'Quickscan ingevuld' },
  { type: 'todo', label: 'Taken aan mij', icon: '✅', trigger: 'Iemand anders wijst taak toe' },
  { type: 'ticket', label: 'Tickets', icon: '🎫', trigger: 'Nieuw ticket aangemaakt' },
  { type: 'project', label: 'Project updates', icon: '📁', trigger: 'Status wijziging' },
  { type: 'project_note', label: 'Project notities', icon: '📝', trigger: 'Ander toevoegt notitie' },
  { type: 'quote', label: 'Offertes', icon: '💰', trigger: 'Binnenkort beschikbaar' },
  { type: 'inspection', label: 'Inspecties', icon: '🔍', trigger: 'Binnenkort beschikbaar' },
  { type: 'calendar', label: 'Agenda herinneringen', icon: '📅', trigger: 'Binnenkort beschikbaar' },
  { type: 'location', label: 'Locatie wijzigingen', icon: '📍', trigger: 'Binnenkort beschikbaar' },
  { type: 'battery', label: 'Batterij allocaties', icon: '🔋', trigger: 'Binnenkort beschikbaar' },
];

export const NotificationPreferencesCard: React.FC = () => {
  const {
    preferences,
    loading,
    updating,
    updatePreference,
    toggleAll,
    updateGlobalSettings,
  } = useNotificationPreferences();

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notificatie Voorkeuren
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!preferences) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-3 sm:pb-6">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
          Notificatie Voorkeuren
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 sm:space-y-6 px-3 sm:px-6">
        {/* Toggle All Buttons */}
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => toggleAll('inapp', true)}
            disabled={updating}
            className="text-xs sm:text-sm"
          >
            In-App Aan
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => toggleAll('inapp', false)}
            disabled={updating}
            className="text-xs sm:text-sm"
          >
            In-App Uit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => toggleAll('push', true)}
            disabled={updating}
            className="text-xs sm:text-sm"
          >
            Push Aan
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => toggleAll('push', false)}
            disabled={updating}
            className="text-xs sm:text-sm"
          >
            Push Uit
          </Button>
        </div>

        <Separator />

        {/* Notification Types Table */}
        <div className="space-y-1">
          {/* Header - hidden on mobile, shown on larger screens */}
          <div className="hidden sm:grid grid-cols-[1fr_60px_60px] sm:grid-cols-[1fr_80px_80px] gap-2 sm:gap-4 pb-2 border-b text-sm font-medium text-muted-foreground">
            <div>Type</div>
            <div className="text-center">In-App</div>
            <div className="text-center">Push</div>
          </div>

          {/* Mobile header */}
          <div className="sm:hidden grid grid-cols-[1fr_50px_50px] gap-1 pb-2 border-b text-xs font-medium text-muted-foreground">
            <div>Type</div>
            <div className="text-center">App</div>
            <div className="text-center">Push</div>
          </div>

          {/* Rows */}
          {NOTIFICATION_TYPES.map(({ type, label, icon, trigger }) => (
            <div
              key={type}
              className="grid grid-cols-[1fr_50px_50px] sm:grid-cols-[1fr_80px_80px] gap-1 sm:gap-4 py-2 items-center border-b last:border-0"
            >
              <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                <span className="text-sm sm:text-lg flex-shrink-0">{icon}</span>
                <div className="min-w-0">
                  <span className="text-xs sm:text-sm block truncate">{label}</span>
                  <span className="text-[10px] sm:text-xs text-muted-foreground block truncate">{trigger}</span>
                </div>
              </div>
              <div className="flex justify-center">
                <Switch
                  checked={preferences[`${type}_inapp` as keyof typeof preferences] as boolean}
                  onCheckedChange={(checked) => updatePreference(type, 'inapp', checked)}
                  disabled={updating}
                  className="scale-90 sm:scale-100"
                />
              </div>
              <div className="flex justify-center">
                <Switch
                  checked={preferences[`${type}_push` as keyof typeof preferences] as boolean}
                  onCheckedChange={(checked) => updatePreference(type, 'push', checked)}
                  disabled={updating}
                  className="scale-90 sm:scale-100"
                />
              </div>
            </div>
          ))}
        </div>

        <Separator />

        {/* Info about notification triggers */}
        <div className="p-3 rounded-lg bg-muted/50 border border-border">
          <p className="text-xs text-muted-foreground">
            <strong>Let op:</strong> Je ontvangt geen meldingen voor acties die je zelf uitvoert. 
            Alleen als iemand anders een taak aan jou toewijst of een notitie toevoegt krijg je een melding.
          </p>
        </div>

        <Separator />

        {/* Global Settings */}
        <div className="space-y-4">
          <h4 className="text-xs sm:text-sm font-medium">Algemene Instellingen</h4>
          
          {/* Quiet Hours */}
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="space-y-0.5 flex-1 min-w-0">
                <Label className="text-xs sm:text-sm">🌙 Stille uren</Label>
                <p className="text-[10px] sm:text-xs text-muted-foreground">
                  Geen push tijdens deze uren
                </p>
              </div>
              <Switch
                checked={preferences.quiet_hours_enabled}
                onCheckedChange={(checked) =>
                  updateGlobalSettings({ quiet_hours_enabled: checked })
                }
                disabled={updating}
                className="scale-90 sm:scale-100"
              />
            </div>

            {preferences.quiet_hours_enabled && (
              <div className="flex items-center gap-2 sm:gap-3 pl-4 sm:pl-6">
                <div className="flex-1">
                  <Label className="text-[10px] sm:text-xs text-muted-foreground">Van</Label>
                  <Input
                    type="time"
                    value={preferences.quiet_hours_start}
                    onChange={(e) =>
                      updateGlobalSettings({ quiet_hours_start: e.target.value })
                    }
                    disabled={updating}
                    className="h-8 sm:h-9 text-xs sm:text-sm"
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-[10px] sm:text-xs text-muted-foreground">Tot</Label>
                  <Input
                    type="time"
                    value={preferences.quiet_hours_end}
                    onChange={(e) =>
                      updateGlobalSettings({ quiet_hours_end: e.target.value })
                    }
                    disabled={updating}
                    className="h-8 sm:h-9 text-xs sm:text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Sound */}
          <div className="flex items-center justify-between gap-2">
            <div className="space-y-0.5 flex-1 min-w-0">
              <Label className="text-xs sm:text-sm">🔊 Geluid</Label>
              <p className="text-[10px] sm:text-xs text-muted-foreground">
                Speel geluid af bij notificaties
              </p>
            </div>
            <Switch
              checked={preferences.sound_enabled}
              onCheckedChange={(checked) =>
                updateGlobalSettings({ sound_enabled: checked })
              }
              disabled={updating}
              className="scale-90 sm:scale-100"
            />
          </div>

          {/* Vibration */}
          <div className="flex items-center justify-between gap-2">
            <div className="space-y-0.5 flex-1 min-w-0">
              <Label className="text-xs sm:text-sm">📳 Trillen</Label>
              <p className="text-[10px] sm:text-xs text-muted-foreground">
                Trilling bij belangrijke notificaties
              </p>
            </div>
            <Switch
              checked={preferences.vibration_enabled}
              onCheckedChange={(checked) =>
                updateGlobalSettings({ vibration_enabled: checked })
              }
              disabled={updating}
              className="scale-90 sm:scale-100"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
